import React, { useEffect, useMemo, useState } from 'react';
import escortIcon from '../../assets/icons/ic_escort.svg'
import IconTint from 'react-icon-tint';
import Dropdown from '../Dropdown';
import LocationSelectionGrid from '../LocationSelectionGrid';
import { useAtom } from 'jotai';
import { currentRobot, escortFrom, escortName, escortTo } from '../store/DataStore';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import ModalShell from './ModalShell';
import VoiceTextButton from './VoiceTextButton';
import { HOME_BASE_LOCATION_NAMES, getSortedVisibleLocations, getVisibleLocationOrDefault } from './locationVisibility';

const LOCATION_GRID_THRESHOLD = 30;

const EscortPopup = ({ isOpen, children, onClose, onSend, locations, currentLocation, kioskLocation }) => {
    const [show, setShow] = useState(isOpen);
    const [selectedFromSaved, setSelectedFromSaved] = useAtom(escortFrom);
    const [selectedToSaved, setSelectedToSaved] = useAtom(escortTo);
    const [selectedName, setSelectedName] = useAtom(escortName);
    const [isDropdownTouched, setIsDropdownTouched] = useState(false)
    const [savedRobot] = useAtom(currentRobot);
    const sortedLocations = useMemo(() => getSortedVisibleLocations(locations, HOME_BASE_LOCATION_NAMES), [locations]);
    const defaultFromLocation = getVisibleLocationOrDefault(kioskLocation, sortedLocations);
    const defaultToLocation = getVisibleLocationOrDefault(currentLocation, sortedLocations);

    useEffect(() => {
        if (isOpen) {
            console.log("My current location is " + currentLocation);
            //setSelectedFrom(currentLocation)
            setShow(true);
            if(!isDropdownTouched){
                setSelectedFromSaved(defaultFromLocation)
                setSelectedToSaved(defaultToLocation)
            }
        } else {
            setShow(false);
            setTimeout(onClose, 200);
        }
    }, [isOpen, onClose, currentLocation, kioskLocation, isDropdownTouched, defaultFromLocation, defaultToLocation, setSelectedFromSaved, setSelectedToSaved]);

    const useLocationGrid = sortedLocations.length < LOCATION_GRID_THRESHOLD;

    const setIsCurrent = async (isCurrent) => {
        const docPath = `Companies/Zeeno_Dev/Settings/${savedRobot}/Controller/actions`;
        const docRef = doc(db, docPath);
        
        try {
          await updateDoc(docRef, {
            is_current: isCurrent,
          });
        } catch (error) {
          console.error('error updated location: ', error);
        }
    }

    const handleClose = () => {
        setShow(false);
        setTimeout(onClose, 200);
        setSelectedFromSaved(defaultFromLocation)
        setSelectedToSaved(defaultToLocation)
        setSelectedName('');
    };

    const handleSend = () => {
        onSend();
        setShow(false);
        setTimeout(onClose, 200);
        setSelectedFromSaved(getVisibleLocationOrDefault(currentLocation, sortedLocations))
        setSelectedToSaved(sortedLocations[0] || '')
        setSelectedName('');

        console.log("Selected from saved is " + selectedFromSaved);
        if(selectedFromSaved === currentLocation) {
            setIsCurrent(true);
        } else {
            setIsCurrent(false);
        }
    }

    const setSelectedFrom = (item) => {
        setIsDropdownTouched(true);
        setSelectedFromSaved(item);
    }

    const setSelectedTo = (item) => {
        setIsDropdownTouched(true);
        setSelectedToSaved(item);
    }

    const canSend = selectedName !== '' &&
        selectedFromSaved !== selectedToSaved &&
        sortedLocations.includes(selectedFromSaved) &&
        sortedLocations.includes(selectedToSaved);

    return (
        <ModalShell isMounted={show || isOpen} isOpen={show} onClose={handleClose} maxWidth="md">
            <div className="-mt-2 flex flex-col items-center text-center">
                {children}
            </div>
            <div className="mt-5 flex flex-col gap-4">
                <div>
                    <label htmlFor="escort-name" className="block font-heebo text-md font-light">Name</label>
                    <div className="relative mt-1">
                        <input
                            id="escort-name"
                            className="h-11 w-full rounded-md bg-[#3C5BD2] bg-opacity-10 px-3 pr-12 outline-none font-heebo font-light"
                            placeholder="Type Name..."
                            value={selectedName}
                            onChange={(e) => setSelectedName(e.target.value)}
                        />
                        <VoiceTextButton
                            value={selectedName}
                            onChange={setSelectedName}
                            ariaLabel="Dictate escort name"
                            isActive={show && isOpen}
                        />
                    </div>
                </div>
                {useLocationGrid ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <span className="mb-2 block font-heebo text-md font-light">From</span>
                            <LocationSelectionGrid
                                locations={sortedLocations}
                                selectedLocation={selectedFromSaved}
                                onSelect={setSelectedFrom}
                                columnsClass="grid-cols-1 md:grid-cols-2"
                                buttonClassName="w-full min-w-0"
                                emptyMessage="No locations available"
                            />
                        </div>
                        <div>
                            <span className="mb-2 block font-heebo text-md font-light">To</span>
                            <LocationSelectionGrid
                                locations={sortedLocations}
                                selectedLocation={selectedToSaved}
                                onSelect={setSelectedTo}
                                columnsClass="grid-cols-1 md:grid-cols-2"
                                buttonClassName="w-full min-w-0"
                                emptyMessage="No locations available"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <label className="block">
                            <span className="font-heebo text-md font-light">From</span>
                            <Dropdown
                                list={sortedLocations}
                                setSelected={(item) => setSelectedFrom(item)}
                                selectedItem={selectedFromSaved}
                                fullWidth={true}
                            />
                        </label>
                        <label className="block">
                            <span className="font-heebo text-md font-light">To</span>
                            <Dropdown
                                list={sortedLocations}
                                setSelected={(item) => setSelectedTo(item)}
                                selectedItem={selectedToSaved}
                                fullWidth={true}
                            />
                        </label>
                    </div>
                )}
            </div>
            <div className="mt-8 flex justify-center">
                {canSend ? (
                    <button
                        type="button"
                        className="flex w-full items-center justify-center rounded-3xl bg-[#152FF5] px-5 py-2 text-white sm:w-auto"
                        onClick={handleSend}
                    >
                        <span className="font-heebo">Confirm Escort</span>
                        <IconTint src={escortIcon} className="ml-2 h-5" color="#FFFFFF" />
                    </button>
                ) : (
                    <div className="flex w-full items-center justify-center rounded-3xl bg-[#152FF5] px-5 py-2 opacity-50 sm:w-auto">
                        <span className="font-heebo text-white">Confirm Escort</span>
                        <IconTint src={escortIcon} className="ml-2 h-5" color="#FFFFFF" />
                    </div>
                )}
            </div>
        </ModalShell>
    );
};

export default EscortPopup;
